/**
 * ServerPaginationControls Component
 * Displays pagination controls for server-side paginated data.
 */
import React from 'react';
import { Button, ButtonGroup, Spinner, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

export const ServerPaginationControls = ({
    currentPage,
    totalPages,
    totalProducts,
    onPrevPage,
    onNextPage,
    onGoToPage,
    loading,
    pageSize = 100,
    onPageSizeChange,
    pageSizeOptions = [25, 50, 100]
}) => {
    const start = totalProducts > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const end = Math.min(currentPage * pageSize, totalProducts);

    // Generate page numbers to show (current +/- 2, plus first and last)
    const getPageNumbers = () => {
        if (totalPages <= 7) {
            // Show all pages if 7 or fewer
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const pages = new Set();

        // Always include first page
        pages.add(1);

        // Include pages around current
        for (let i = Math.max(2, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) {
            pages.add(i);
        }

        // Always include last page
        pages.add(totalPages);

        return Array.from(pages).sort((a, b) => a - b);
    };

    const pageNumbers = getPageNumbers();

    // Check if there's a gap between consecutive page numbers
    const hasGap = (index) => {
        if (index === 0) return false;
        return pageNumbers[index] - pageNumbers[index - 1] > 1;
    };

    return (
        <div
            className="d-flex justify-content-between align-items-center px-3 py-2 border-top"
            style={{ backgroundColor: '#f8f9fa' }}
        >
            {/* Left side: Showing text and page size selector */}
            <div className="d-flex align-items-center">
                <span className="text-muted" style={{ fontSize: '12px' }}>
                    {totalProducts > 0 ? (
                        <>Showing <strong>{start.toLocaleString()}-{end.toLocaleString()}</strong> of <strong>{totalProducts.toLocaleString()}</strong> products</>
                    ) : (
                        'No products found'
                    )}
                </span>

                {/* Page size selector */}
                <div className="d-flex align-items-center ms-3">
                    <span className="text-muted me-2" style={{ fontSize: '12px' }}>
                        Show:
                    </span>
                    <Form.Select
                        size="sm"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
                        disabled={loading}
                        style={{ width: '80px' }}
                        aria-label="Items per page"
                    >
                        {pageSizeOptions.map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </Form.Select>
                </div>
            </div>

            {/* Right side: Pagination controls */}
            <div className="d-flex align-items-center gap-2">
                {loading && <Spinner animation="border" size="sm" className="me-2" />}

                <ButtonGroup size="sm">
                    <Button
                        variant="outline-secondary"
                        onClick={onPrevPage}
                        disabled={currentPage <= 1 || loading}
                        title="Previous page"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </Button>

                    {pageNumbers.map((page, index) => (
                        <React.Fragment key={page}>
                            {hasGap(index) && (
                                <Button variant="outline-secondary" disabled style={{ cursor: 'default' }}>
                                    ...
                                </Button>
                            )}
                            <Button
                                variant={page === currentPage ? 'primary' : 'outline-secondary'}
                                onClick={() => onGoToPage(page)}
                                disabled={loading || page === currentPage}
                            >
                                {page}
                            </Button>
                        </React.Fragment>
                    ))}

                    <Button
                        variant="outline-secondary"
                        onClick={onNextPage}
                        disabled={currentPage >= totalPages || loading}
                        title="Next page"
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Button>
                </ButtonGroup>
            </div>
        </div>
    );
};

export default ServerPaginationControls;
